import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Slider } from '@fragments';
import { IconBorderRadius, IconFlipHorizontal, IconFlipVertical, IconPhotoPlus, IconUpload, IconUserCircle } from '@tabler/icons-react';
import ColorField from '../../ColorPicker';
import { PopoverAction, Toggle } from '../controls';
import { isPlaceholderSource } from '../../../../model/schema';

// An image layer is a frame with a photo paint (Figma's image fill): its swatch opens the
// photo editor (send or pick another photo, fill or fit, zoom, adjustments, move it inside
// the frame). Flips and corner rounding stay on the bar. An empty place (2.10.0, a
// `placeholder` source) asks for its image instead: first a photo of the creator's own
// (recommended: real photos keep the profile authentic), then one from the gallery or, on an
// avatar, the creator's profile picture (`avatar`, an image source).
export default function ImageSection({ layer, update, photo, avatar = null }) {
  const { t } = useTranslation('artEditor');
  const input = useRef(null);
  const key = name => ({ key: `${layer.id}:${name}` });
  const paint = { type: 'image', source: layer.source, fit: layer.fit, focus_x: layer.focus_x, focus_y: layer.focus_y, zoom: layer.zoom, adjust: layer.adjust };
  const empty = isPlaceholderSource(layer.source);
  const profilePicture = empty && layer.binding === 'avatar' && avatar;
  return <>
    {empty ? <>
      {profilePicture && <Button size="small" variant="rounded" color="inverse" preventLoading startIcon={<IconUserCircle />}
        onClick={() => update({ source: avatar, focus_x: 0.5, focus_y: 0.5, zoom: 1 }, { key: null })}>{t('useProfilePicture')}</Button>}
      <Button size="small" variant="rounded" color={profilePicture ? 'overlay' : 'inverse'} preventLoading startIcon={<IconUpload />} tooltip={t('uploadOwnPhotoHint')}
        onClick={() => input.current?.click()}>{t('uploadOwnPhoto')}</Button>
      <Button size="small" variant="rounded" color="overlay" preventLoading startIcon={<IconPhotoPlus />} onClick={photo.onGallery}>{t('chooseImage')}</Button>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden
        onChange={event => { const files = [...event.target.files]; event.target.value = ''; if (files.length) photo.onUpload(files.slice(0, 1)); }} />
    </> : <ColorField label={t('imageFill')} value={paint} modes={['image']} image={photo}
      onChange={next => next && update({ source: next.source, fit: next.fit, focus_x: next.focus_x, focus_y: next.focus_y, zoom: next.zoom, adjust: next.adjust }, key('framing'))} />}
    <Toggle icon={IconFlipHorizontal} label={t('flipHorizontal')} active={layer.flip_x} onClick={() => update({ flip_x: !layer.flip_x }, { key: null })} />
    <Toggle icon={IconFlipVertical} label={t('flipVertical')} active={layer.flip_y} onClick={() => update({ flip_y: !layer.flip_y }, { key: null })} />
    <PopoverAction icon={IconBorderRadius} label={t('cornerRadius')}>
      <Slider label={t('cornerRadius')} min={0} max={Math.round(Math.min(layer.width, layer.height) / 2)} value={Math.min(layer.corner_radius, Math.round(Math.min(layer.width, layer.height) / 2))}
        onChange={(_, value) => update({ corner_radius: value }, key('corner_radius'))} />
    </PopoverAction>
  </>;
}
